import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CreditCard, FileText, Loader2, MapPin, Minus, Phone, Plus, Search, ShoppingCart, Tag, Ticket, Trash2, User, X } from 'lucide-react';
import api, { erpAPI, adminAPI } from '../../services/api';
import SearchableSelect from '../../components/admin/SearchableSelect';
import Toast from '../../components/admin/Toast';
import { paymentAccountOptions } from '../../utils/erpHelpers';
import './ERPManagement.css';
import './PurchaseManagement.css';
import './POSWorkspace.css';

const money = (v: any) => `\u09F3${Number(v || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const rows = (p: any): any[] => Array.isArray(p) ? p : (p?.results || []);
const dt = (v: any) => v ? new Date(v).toLocaleString('en-BD') : '-';
const errorText = (e: any) => e?.response?.data?.detail || e?.message || 'Request failed';

type SaleRow = {
  product_variant: number;
  product: number;
  product_name: string;
  sku: string;
  color: string;
  size: string;
  available: number;
  unit_price: number;
  normal_price: number;
  quantity: number;
};

type SearchGroup = { product: number; name: string; variants: any[] };

export default function POSWorkspace({ locationFilter, locations: externalLocations }: { locationFilter?: string; locations?: any[] }) {
  const [data, setData] = useState<Record<string, any>>({ products: [], inventory: [], 'shop-prices': [], 'payment-accounts': [] });
  const [locations, setLocations] = useState<any[]>(externalLocations || []);
  const [shop, setShop] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  // ── Customer ──
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [custResults, setCustResults] = useState<any[]>([]);
  const [custOpen, setCustOpen] = useState(false);
  const custDebounce = useRef<any>(null);
  const custRef = useRef<HTMLDivElement>(null);
  const customerRef = useRef<HTMLInputElement>(null);

  const [reference, setReference] = useState('');

  // ── Payment / coupon ──
  const [paymentMethod, setPaymentMethod] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState<{ code: string; discount: number } | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponChecking, setCouponChecking] = useState(false);

  // ── Product search / sale items ──
  const [productSearch, setProductSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [cart, setCart] = useState<SaleRow[]>([]);
  const [confirmClear, setConfirmClear] = useState(false);

  const [saving, setSaving] = useState(false);
  const [showInvoice, setShowInvoice] = useState<any>(null);
  const [showShopPicker, setShowShopPicker] = useState(false);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'F2') { e.preventDefault(); searchInputRef.current?.focus(); setShowSearch(true); }
      if (e.key === 'F4') { e.preventDefault(); customerRef.current?.focus(); }
      if (e.key === 'F8') { e.preventDefault(); document.getElementById('pos-payment-account')?.querySelector('input')?.focus(); }
      if (e.key === 'Escape') { setShowSearch(false); setCustOpen(false); }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); completeSale(); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && (e.ctrlKey || e.metaKey)) { e.preventDefault(); if (cart.length) setConfirmClear(true); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // ── Shop selection: from navbar location filter, or modal when "All Locations" ──
  useEffect(() => {
    if (!locationFilter || locationFilter === 'all' || locationFilter === '') {
      if (!shop) setShowShopPicker(true);
      return;
    }
    const lid = Number(locationFilter);
    const loc = locations.find((l: any) => l.id === lid);
    if (loc && loc.location_type === 'SHOP') { setShop(String(lid)); setShowShopPicker(false); }
    else if (!shop) setShowShopPicker(true);
  }, [locationFilter, locations, shop]);

  const selectShop = (shopId: string) => {
    setShop(shopId);
    setShowShopPicker(false);
    sessionStorage.setItem('erp-location-filter', shopId);
    window.dispatchEvent(new CustomEvent('erp-location-change', { detail: { location: shopId } }));
  };

  const shops = useMemo(() => locations.filter((l: any) => l.location_type === 'SHOP' && l.is_active), [locations]);

  // Clear cart when shop changes (prices/inventory differ per shop)
  useEffect(() => { setCart([]); setConfirmClear(false); setCouponApplied(null); setCouponError(null); setCouponCode(''); setPaymentMethod(''); }, [shop]);

  // ── Data Loading: refetch when shop changes ──
  const loadData = useCallback(() => {
    setLoading(true);
    const sid = shop ? Number(shop) : undefined;
    Promise.all([
      externalLocations ? Promise.resolve() : erpAPI.list('locations', {}).then(r => setLocations(rows(r.data))),
      erpAPI.list('variants', { page_size: 200 }).then(r => { const v = rows(r.data); setData(d => ({ ...d, products: v })); }),
      erpAPI.list('inventory', sid ? { page_size: 500, location: sid } : { page_size: 500 }).then(r => setData(d => ({ ...d, inventory: rows(r.data) }))),
      erpAPI.list('shop-prices', sid ? { page_size: 500, shop: sid } : { page_size: 500 }).then(r => setData(d => ({ ...d, 'shop-prices': rows(r.data) }))),
      erpAPI.list('payment-accounts', sid ? { location: sid } : {}).then(r => setData(d => ({ ...d, 'payment-accounts': rows(r.data) }))),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [shop, externalLocations]);

  useEffect(() => { loadData(); }, [loadData]);

  // Close search/customer dropdowns on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowSearch(false);
      if (custRef.current && !custRef.current.contains(e.target as Node)) setCustOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Computed ──
  const payOpts = useMemo(() => paymentAccountOptions(data['payment-accounts'] || [], { excludeCod: true }), [data['payment-accounts']]);

  // ── Product search (client-side, scoped to shop stock) ──
  const matchingVariants = useMemo(() => {
    const q = productSearch.toLowerCase().trim();
    if (!q) return [];
    const sid = Number(shop);
    return data.products.map((v: any) => {
      const stock = (data.inventory || []).find((s: any) => s.location === sid && s.product_variant === v.id);
      const sp = (data['shop-prices'] || []).find((p: any) => p.shop === sid && p.product_variant === v.id);
      const normal = Number(sp?.normal_price || 0);
      const effective = sp?.effective_price !== undefined ? Number(sp.effective_price) : normal;
      return { ...v, available: stock?.available_quantity || 0, unit_price: effective, normal_price: normal };
    }).filter((v: any) => {
      const text = [v.product_name || v.product?.name_en, v.product?.sku, v.sku, v.barcode, v.color, v.size].filter(Boolean).join(' ').toLowerCase();
      return text.includes(q);
    }).filter((v: any) => (shop ? v.available > 0 : true)).slice(0, 30);
  }, [data, productSearch, shop]);

  const productGroups = useMemo(() => {
    const map = new Map<number, SearchGroup>();
    for (const v of matchingVariants) {
      const pid = v.product || v.product_id;
      const group = map.get(pid);
      if (group) group.variants.push(v);
      else map.set(pid, { product: pid, name: v.product_name || v.product?.name_en || 'Product', variants: [v] });
    }
    return [...map.values()];
  }, [matchingVariants]);

  const addRow = (v: any, qty = 1) => {
    if (!shop) { setMessage({ type: 'warning', text: 'Select a shop first.' }); return; }
    setCart(items => {
      const existing = items.find(i => i.product_variant === v.id);
      if (existing) {
        const q = Math.max(1, Math.min(existing.quantity + qty, v.available > 0 ? v.available : existing.available));
        return items.map(i => i.product_variant === v.id ? { ...i, quantity: q } : i);
      }
      return [...items, {
        product_variant: v.id, product: v.product || v.product_id,
        product_name: v.product_name || v.product?.name_en || 'Item',
        sku: v.sku || '', color: v.color || '', size: v.size || '',
        available: v.available || 0, unit_price: v.unit_price, normal_price: v.normal_price || v.unit_price,
        quantity: 1,
      }];
    });
  };

  const addAllVariants = (group: SearchGroup) => {
    group.variants.forEach(v => addRow(v, 1));
    setProductSearch('');
    setShowSearch(false);
    setMessage({ type: 'success', text: `Added ${group.variants.length} variant(s) of ${group.name}.` });
  };

  const onSelectResult = (kind: 'group' | 'variant', payload: any) => {
    if (kind === 'group') addAllVariants(payload as SearchGroup);
    else addRow(payload);
    setHighlightedIdx(-1);
    setProductSearch('');
    setShowSearch(false);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleSearchKey = (e: React.KeyboardEvent) => {
    const flat = buildFlatResults();
    if (!showSearch || !flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIdx(p => (p < flat.length - 1 ? p + 1 : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIdx(p => (p > 0 ? p - 1 : flat.length - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (highlightedIdx >= 0) selectFlat(flat[highlightedIdx]); else if (flat.length) selectFlat(flat[0]); }
    else if (e.key === 'Escape') { e.preventDefault(); setShowSearch(false); setHighlightedIdx(-1); }
  };

  const buildFlatResults = useCallback((): { kind: 'group' | 'variant'; payload: any; label: string; sub: string; color: string; size: string; sku: string; available: number }[] => {
    const out: { kind: 'group' | 'variant'; payload: any; label: string; sub: string; color: string; size: string; sku: string; available: number }[] = [];
    for (const g of productGroups) {
      if (g.variants.length > 1) {
        out.push({ kind: 'group', payload: g, label: g.name, sub: `Add all ${g.variants.length} variants`, color: '', size: '', sku: '', available: 0 });
      }
    }
    for (const v of matchingVariants) {
      const vLabel = v.product_name || v.product?.name_en || 'Item';
      const chips = [v.color, v.size].filter(Boolean).join(' • ');
      out.push({ kind: 'variant', payload: v, label: vLabel, sub: chips, color: v.color || '', size: v.size || '', sku: v.sku || '', available: v.available || 0 });
    }
    return out;
  }, [productGroups, matchingVariants]);

  const selectFlat = (entry: any) => onSelectResult(entry.kind, entry.payload);

  // ── Customer search ──
  const loadCustomers = useCallback(async (q: string) => {
    if (!q.trim()) { setCustResults([]); setCustOpen(false); return; }
    try {
      const res = await adminAPI.getCustomers({ search: q.trim(), page_size: 8 });
      const list = rows(res.data);
      setCustResults(list);
      setCustOpen(list.length > 0);
      const exact = list.find((c: any) => String(c.phone) === q.trim());
      if (exact) { setCustomerName(exact.full_name || [exact.first_name, exact.last_name].filter(Boolean).join(' ')); }
    } catch { setCustResults([]); setCustOpen(false); }
  }, []);

  const onPhoneChange = (value: string) => {
    setCustomerPhone(value);
    setCustomerName('');
    if (custDebounce.current) clearTimeout(custDebounce.current);
    custDebounce.current = setTimeout(() => loadCustomers(value), 220);
  };

  const selectCustomer = (c: any) => {
    setCustomerPhone(String(c.phone || ''));
    setCustomerName(c.full_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || '');
    setCustOpen(false);
    setCustResults([]);
    customerRef.current?.focus();
  };

  // ── Cart ops ──
  const updateQty = (vid: number, value: string) => {
    const q = Math.max(1, Math.round(Number(value) || 0));
    setCart(items => items.map(i => i.product_variant === vid ? { ...i, quantity: Math.min(q, Math.max(1, i.available)) } : i));
  };
  const removeRow = (vid: number) => { setCart(items => items.filter(i => i.product_variant !== vid)); };
  const clearCart = () => { setCart([]); setConfirmClear(false); };

  // ── Totals ──
  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.quantity * i.normal_price, 0), [cart]);
  const autoDiscount = useMemo(() => cart.reduce((s, i) => s + i.quantity * (i.normal_price - i.unit_price), 0), [cart]);
  const couponDiscount = couponApplied?.discount || 0;
  const totalDiscount = autoDiscount + couponDiscount;
  const total = Math.max(0, subtotal - totalDiscount);

  // ── Coupon ──
  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    setCouponChecking(true);
    setCouponError(null);
    try {
      const res = await api.post('/coupons/validate_code/', {
        code, channel: 'SHOP', location: Number(shop),
        purchase_amount: subtotal - autoDiscount,
        line_items: cart.map(i => ({ product_id: i.product, subtotal: i.quantity * i.unit_price })),
      });
      if (res.data?.valid && Number(res.data.discount_amount) > 0) {
        setCouponApplied({ code, discount: Number(res.data.discount_amount) });
        setCouponError(null);
        setMessage({ type: 'success', text: `Coupon applied: ${money(Number(res.data.discount_amount))} off` });
      } else {
        setCouponApplied(null);
        setCouponError(res.data?.error || 'Coupon is not valid for this sale.');
      }
    } catch (e: any) {
      setCouponApplied(null);
      setCouponError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to validate coupon.');
    } finally { setCouponChecking(false); }
  };

  const removeCoupon = () => { setCouponApplied(null); setCouponCode(''); setCouponError(null); };

  // ── Complete sale ──
  const completeSale = async () => {
    if (saving) return;
    if (!shop) { setMessage({ type: 'warning', text: 'Select a shop first.' }); return; }
    if (!cart.length) { setMessage({ type: 'warning', text: 'Add at least one product to the sale.' }); return; }
    if (!paymentMethod) { setMessage({ type: 'warning', text: 'Please select receiving account.' }); return; }
    if (total <= 0) { setMessage({ type: 'warning', text: 'Total must be positive.' }); return; }
    setSaving(true);
    try {
      const idempotencyKey = `${Date.now()}-${crypto.randomUUID()}`;
      const payload: any = {
        shop: Number(shop), customer_name: customerName || 'Walk-in', customer_phone: customerPhone || '',
        pos_idempotency_key: idempotencyKey,
        items: cart.map(i => ({ product_variant: i.product_variant, quantity: i.quantity })),
        payments: [{ method: Number(paymentMethod), amount: total, reference: reference || '' }],
      };
      if (couponApplied) payload.coupon_code = couponApplied.code;
      const resp = await erpAPI.create('sales', payload);
      const invoice = { ...resp.data, items: [...cart], total, autoDiscount, couponApplied, subtotal };
      setShowInvoice(invoice);
      setMessage({ type: 'success', text: `Sale completed! ${invoice.sale_number || ''} — ${money(total)}` });
      setCart([]); setConfirmClear(false);
      setCustomerPhone(''); setCustomerName(''); setCustResults([]); setCustOpen(false);
      setPaymentMethod(''); setReference('');
      setCouponCode(''); setCouponApplied(null); setCouponError(null);
      setProductSearch(''); setShowSearch(false);
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
    finally { setSaving(false); }
  };

  const printInvoice = (sale: any) => {
    const w = window.open('', '_blank', 'width=800,height=600');
    if (!w) return;
    w.document.write(`<html><head><title>Invoice ${sale.sale_number}</title>
      <style>body{font-family:sans-serif;padding:30px;max-width:400px;margin:auto}
      h1{font-size:16px;margin-bottom:4px;color:#1e293b}
      .meta{font-size:12px;color:#64748b;margin-bottom:16px}
      table{width:100%;border-collapse:collapse;font-size:13px}
      th,td{padding:6px 4px;text-align:left;border-bottom:1px solid #e2e8f0}
      .right{text-align:right}
      .total{font-weight:700;font-size:15px;margin-top:12px;text-align:right}
      .footer{font-size:11px;color:#94a3b8;text-align:center;margin-top:24px;border-top:1px dashed #e2e8f0;padding-top:12px}</style></head><body>
      <h1>POS Invoice</h1>
      <div class="meta">${sale.sale_number || ''}<br>${dt(new Date())}<br>${sale.shop_name || ''}</div>
      <div class="meta">Customer: ${sale.customer_name || 'Walk-in'}${sale.customer_phone ? ' · ' + sale.customer_phone : ''}</div>
      <table><tr><th>Item</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Total</th></tr>
      ${(sale.items || []).map((i: any) => `<tr><td>${i.product_name || i.label || i.product_variant_name || 'Item'}${i.color || i.size ? ' — ' + [i.color, i.size].filter(Boolean).join(' / ') : ''}</td><td class="right">${i.quantity}</td><td class="right">${money(i.unit_price)}</td><td class="right">${money(i.quantity * i.unit_price)}</td></tr>`).join('')}
      </table>
      <div style="font-size:13px;margin-top:8px;text-align:right">
        <div>Subtotal: ${money(sale.subtotal || sale.total_amount || 0)}</div>
        ${sale.autoDiscount > 0 ? `<div style="color:#dc2626">Auto Discounts: -${money(sale.autoDiscount)}</div>` : ''}
        ${sale.couponApplied ? `<div style="color:#dc2626">Coupon (${sale.couponApplied.code}): -${money(sale.couponApplied.discount)}</div>` : ''}
        ${(sale.autoDiscount > 0 || sale.couponApplied) ? `<div style="border-top:1px solid #e2e8f0;padding-top:4px;margin-top:4px">Total Discount: -${money((sale.autoDiscount || 0) + (sale.couponApplied?.discount || 0))}</div>` : ''}
      </div>
      <div class="total">Grand Total: ${money(sale.total_amount || sale.total)}</div>
      <div class="footer">Thank you for your purchase!</div></body></html>`);
    w.document.close();
    w.print();
  };

  if (loading && !data.products.length) return <div className="erp-loading"><Loader2 size={20} className="spin" /> Loading POS workspace…</div>;

  const flatResults = buildFlatResults();

  return (
    <div className="posw-page">
      <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} duration={message?.type === 'error' ? 5000 : 3200} />

      {/* ═══ HEADER: Customer + Checkout (merged) ═══ */}
      <div className="posw-header">
        <div className="posw-top">
        <div className="posw-field posw-phone" ref={custRef} style={{ position: 'relative' }}>
          <label>Customer Phone</label>
          <div className="posw-input-wrap">
            <Phone size={15} style={{ color: '#818cf8', flexShrink: 0 }} />
            <input ref={customerRef} value={customerPhone}
              onChange={e => onPhoneChange(e.target.value)}
              onFocus={() => { if (custResults.length) setCustOpen(true); }}
              placeholder="01XXXXXXXXX" autoComplete="off" />
            {customerPhone && <button type="button" className="posw-clear" onClick={() => { setCustomerPhone(''); setCustomerName(''); setCustResults([]); }}><X size={13} /></button>}
          </div>
          {custOpen && custResults.length > 0 && (
            <div className="posw-cust-dropdown">
              {custResults.map((c: any) => (
                <button key={c.id} type="button" onClick={() => selectCustomer(c)}>
                  <span className="posw-cust-phone">{c.phone || '—'}</span>
                  <span className="posw-cust-name">{c.full_name || c.username || ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="posw-field">
          <label>Customer Name</label>
          <div className="posw-input-wrap">
            <User size={15} style={{ color: '#818cf8', flexShrink: 0 }} />
            <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Walk-in / customer name" />
          </div>
        </div>

        <div className="posw-field posw-ref">
          <label>Reference</label>
          <div className="posw-input-wrap">
            <FileText size={15} style={{ color: '#818cf8', flexShrink: 0 }} />
            <input value={reference} onChange={e => setReference(e.target.value)} placeholder="Optional" />
          </div>
        </div>

        <div className="posw-kbd">
          <span className="posw-kbd-title">Shortcuts</span>
          <span><kbd>F2</kbd> Search</span>
          <span><kbd>F4</kbd> Customer</span>
          <span><kbd>Ctrl+Enter</kbd> Complete</span>
        </div>
      </div>

      <div className="posw-checkout">
        <div className="posw-checkout-grid">
          <div className="posw-co-group">
            <div className="posw-co-head"><span className="posw-co-icon"><Ticket size={13} /></span> Coupon</div>
            {couponApplied ? (
              <div className="posw-coupon-applied">
                <Tag size={14} /> {couponApplied.code}
                <strong>-{money(couponApplied.discount)}</strong>
                <button type="button" onClick={removeCoupon} title="Remove coupon"><X size={13} /></button>
              </div>
            ) : (
              <div className="posw-coupon-row">
                <input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} placeholder="Add coupon code" />
                <button type="button" className="posw-coupon-btn" disabled={!couponCode.trim() || couponChecking} onClick={applyCoupon}>
                  {couponChecking ? <Loader2 size={13} className="spin" /> : 'Apply'}
                </button>
              </div>
            )}
            {couponError && <div className="posw-coupon-error">{couponError}</div>}
          </div>

          <div className="posw-co-group">
            <div className="posw-co-head"><span className="posw-co-icon"><CreditCard size={13} /></span> Payment</div>
            <div id="pos-payment-account" className="posw-pay-select">
              <SearchableSelect value={paymentMethod} onChange={setPaymentMethod} placeholder="Select receiving account"
                options={payOpts} />
            </div>
            <div className="posw-pay-note">Received amount = sale total · recorded automatically</div>
          </div>
        </div>

        <div className="posw-total-panel">
          <div className="posw-total-row"><span>Subtotal</span><b>{money(subtotal)}</b></div>
          {autoDiscount > 0 && <div className="posw-total-row"><span>Auto Discount</span><b className="posw-negative">-{money(autoDiscount)}</b></div>}
          {couponDiscount > 0 && <div className="posw-total-row"><span>Coupon</span><b className="posw-negative">-{money(couponDiscount)}</b></div>}
          <div className="posw-total-row posw-grand"><span>Total</span><b>{money(total)}</b></div>
          <div className="posw-complete">
            {confirmClear && cart.length > 0 && (
              <button type="button" className="posw-clear-btn" onClick={clearCart}>Confirm clear?</button>
            )}
            {cart.length > 0 && !confirmClear && (
              <button type="button" className="posw-clear-btn" onClick={() => setConfirmClear(true)} title="Clear cart (Ctrl+Del)">Clear</button>
            )}
            <button type="button" className="posw-complete-btn" disabled={!cart.length || !paymentMethod || saving} onClick={completeSale}>
              <ShoppingCart size={17} />
              {!cart.length ? 'Add items' : !paymentMethod ? 'Select receiving account' : saving ? <><Loader2 size={15} className="spin" /> Completing…</> : `Complete Sale — ${money(total)}`}
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* ═══ MIDDLE: Product & Variant Selection / Sale Items ═══ */}
      <div className="posw-middle">
        <div className="posw-search-bar">
          <div className="posw-search-box" ref={searchRef}>
            <Search size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
            <input ref={searchInputRef} id="pos-product-search" value={productSearch}
              onChange={e => { setProductSearch(e.target.value); setShowSearch(true); setHighlightedIdx(-1); }}
              onFocus={() => { if (productSearch) setShowSearch(true); }}
              onKeyDown={handleSearchKey}
              placeholder="F2 — Search product, SKU, barcode…" autoComplete="off" />
            {productSearch && <button type="button" className="posw-clear" onClick={() => { setProductSearch(''); setShowSearch(false); searchInputRef.current?.focus(); }}><X size={14} /></button>}
            {showSearch && flatResults.length > 0 && (
              <div className="pm-product-results">
                {(() => {
                  const groups = flatResults.filter(r => r.kind === 'group');
                  const variants = flatResults.filter(r => r.kind === 'variant');
                  const variantStart = groups.length;
                  return (
                    <>
                      {groups.length > 0 && (
                        <>
                          <div className="pm-search-section-label">Products (add all variants)</div>
                          {groups.map((r, i) => (
                            <button key={`g-${r.payload.product}`} type="button"
                              className={`pm-product-result ${highlightedIdx === i ? 'pm-option-hl' : ''}`}
                              onClick={() => onSelectResult('group', r.payload)} onMouseEnter={() => setHighlightedIdx(i)}>
                              <strong>{r.label}</strong>
                              <span className="pm-vr-detail">{r.sub}</span>
                            </button>
                          ))}
                        </>
                      )}
                      {variants.length > 0 && (
                        <>
                          <div className="pm-search-section-label">Variants</div>
                          {variants.map((r, i) => {
                            const idx = variantStart + i;
                            return (
                              <button key={`v-${r.payload.id}`} type="button"
                                className={`pm-variant-result ${highlightedIdx === idx ? 'pm-option-hl' : ''}`}
                                onClick={() => onSelectResult('variant', r.payload)} onMouseEnter={() => setHighlightedIdx(idx)}>
                                <span className="pm-vr-product">{r.label}</span>
                                <span className="pm-vr-detail">
                                  {[r.sub, r.sku].filter(Boolean).join(' — ')}
                                  {r.available > 0 && <span className="posw-res-avail"> · {r.available} in stock</span>}
                                </span>
                              </button>
                            );
                          })}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            {showSearch && productSearch && !flatResults.length && (
              <div className="pm-product-results"><div className="pm-empty">No products with stock at this shop match your search.</div></div>
            )}
          </div>
          <div className="posw-search-meta">
            {productGroups.length > 0 && <span>{productGroups.length} product{productGroups.length === 1 ? '' : 's'} match</span>}
          </div>
        </div>

        <div className="posw-table-wrap">
          {cart.length === 0 ? (
            <div className="posw-empty">
              <div className="posw-empty-icon"><ShoppingCart size={30} /></div>
              <strong>Sale items</strong>
              <p>Search above to add products. Prices and available stock follow the shop selected in the top navigation.</p>
              {!shop && <div className="posw-no-shop-banner"><AlertTriangle size={14} /> Select a <strong>Shop</strong> from the Business Location filter in the top navigation to start selling.</div>}
            </div>
          ) : (
            <table className="posw-table">
              <colgroup>
                <col style={{ width: '26%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '44px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Product</th><th>Variant</th><th className="posw-th-num">Available</th>
                  <th className="posw-th-num">Qty</th><th className="posw-th-num">Unit Price</th>
                  <th className="posw-th-num">Discount</th><th className="posw-th-num">Total</th><th></th>
                </tr>
              </thead>
              <tbody>
                {cart.map(i => {
                  const hasDiscount = i.normal_price > i.unit_price;
                  return (
                    <tr key={i.product_variant}>
                      <td className="posw-prod">{i.product_name}</td>
                      <td>{[i.color, i.size].filter(Boolean).join(' / ') || <code>{i.sku}</code>}</td>
                      <td className="posw-td-num"><span style={{ color: i.available < i.quantity ? '#dc2626' : '#475569', fontWeight: 600 }}>{i.available}</span></td>
                      <td className="posw-td-num">
                        <div className="posw-qty-stepper">
                          <button type="button" onClick={() => setCart(items => items.map(x => x.product_variant === i.product_variant ? { ...x, quantity: Math.max(1, x.quantity - 1) } : x))}><Minus size={12} /></button>
                          <input type="number" min={1} max={i.available} value={i.quantity} onChange={e => updateQty(i.product_variant, e.target.value)} />
                          <button type="button" onClick={() => setCart(items => items.map(x => x.product_variant === i.product_variant ? { ...x, quantity: Math.min(i.available, x.quantity + 1) } : x))}><Plus size={12} /></button>
                        </div>
                      </td>
                      <td className="posw-td-num">
                        {hasDiscount
                          ? <><span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>{money(i.normal_price)}</span><span style={{ color: '#dc2626', fontWeight: 700, marginLeft: 4 }}>{money(i.unit_price)}</span></>
                          : <>{money(i.unit_price)}</>}
                      </td>
                      <td className="posw-td-num">{hasDiscount ? <span style={{ color: '#dc2626', fontWeight: 600 }}>-{money(i.quantity * (i.normal_price - i.unit_price))}</span> : '—'}</td>
                      <td className="posw-td-num posw-line-total">{money(i.quantity * i.unit_price)}</td>
                      <td><button type="button" className="posw-row-del" onClick={() => removeRow(i.product_variant)}><Trash2 size={14} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ═══ Shop selection — required when navbar location is "All Locations" ═══ */}
      {showShopPicker && (
        <div className="acct-modal-overlay" onClick={() => shop && setShowShopPicker(false)}>
          <div className="acct-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="acct-modal-header">
              <h3 className="acct-modal-title"><MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Select Shop Location</h3>
            </div>
            <div className="acct-modal-body">
              <p className="pos-invoice-meta" style={{ marginBottom: 12 }}>A sale must always belong to a shop. Choose where this sale happens to continue.</p>
              {shops.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: '.82rem' }}>No accessible shops found for your account.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {shops.map(s => (
                    <button key={s.id} type="button" onClick={() => selectShop(String(s.id))} className="posw-shop-option">
                      <MapPin size={16} />
                      <span>{s.name}</span>
                      <small>{s.location_type}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Invoice preview ═══ */}
      {showInvoice && <div className="acct-modal-overlay" onClick={() => setShowInvoice(null)}>
        <div className="acct-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
          <div className="acct-modal-header">
            <h3 className="acct-modal-title">Invoice — {showInvoice.sale_number || ''}</h3>
            <button className="acct-modal-close" onClick={() => setShowInvoice(null)}><X size={16} /></button>
          </div>
          <div className="acct-modal-body">
            <div style={{ marginBottom: 14, fontSize: '.78rem' }}>
              <div className="pos-invoice-shop">{showInvoice.shop_name || 'Shop'}</div>
              <div className="pos-invoice-meta">{showInvoice.customer_name || 'Walk-in'} · {showInvoice.customer_phone || '-'}</div>
              <div className="pos-invoice-meta">{dt(new Date())}</div>
            </div>
            <div className="pos-invoice-table-wrapper">
              <table className="pos-invoice-table">
                <thead><tr className="pos-invoice-th-row">
                  <th className="pos-invoice-th" style={{ textAlign: 'left' }}>Item</th>
                  <th className="pos-invoice-th" style={{ textAlign: 'center' }}>Qty</th>
                  <th className="pos-invoice-th" style={{ textAlign: 'right' }}>Price</th>
                  <th className="pos-invoice-th" style={{ textAlign: 'right' }}>Total</th>
                </tr></thead>
                <tbody>{(showInvoice.items || []).map((i: any, idx: number) => <tr key={idx}>
                  <td className="pos-invoice-td" style={{ fontWeight: 500 }}>{i.product_name || i.label || i.product_variant_name || 'Item'}{i.color || i.size ? ' — ' + [i.color, i.size].filter(Boolean).join(' / ') : ''}</td>
                  <td className="pos-invoice-td" style={{ textAlign: 'center' }}>{i.quantity}</td>
                  <td className="pos-invoice-td" style={{ textAlign: 'right' }}>{money(i.unit_price)}</td>
                  <td className="pos-invoice-td" style={{ textAlign: 'right', fontWeight: 600 }}>{money(i.quantity * i.unit_price)}</td>
                </tr>)}</tbody>
                <tfoot>
                  <tr><td colSpan={4} className="pos-invoice-tfoot-td" style={{ textAlign: 'right' }}>Subtotal: {money(showInvoice.subtotal || showInvoice.total_amount || 0)}</td></tr>
                  {showInvoice.autoDiscount > 0 && <tr><td colSpan={4} className="pos-invoice-tfoot-td" style={{ textAlign: 'right', color: '#dc2626' }}>Auto Discount: -{money(showInvoice.autoDiscount)}</td></tr>}
                  {showInvoice.couponApplied && <tr><td colSpan={4} className="pos-invoice-tfoot-td" style={{ textAlign: 'right', color: '#dc2626' }}>Coupon ({showInvoice.couponApplied.code}): -{money(showInvoice.couponApplied.discount)}</td></tr>}
                  <tr><td colSpan={4} className="pos-invoice-tfoot-td pos-invoice-grand" style={{ textAlign: 'right', fontWeight: 800 }}>Total Due: {money(showInvoice.total)}</td></tr>
                </tfoot>
              </table>
            </div>
            <div className="acct-form-actions" style={{ marginTop: 16 }}>
              <button className="acct-btn acct-btn-secondary" onClick={() => setShowInvoice(null)}>Close</button>
              <button className="acct-btn acct-btn-primary" onClick={() => printInvoice(showInvoice)}><FileText size={14} /> Print</button>
            </div>
          </div>
        </div>
      </div>}
    </div>
  );
}